import React from 'react';
import Header from '../Header';
import Footer from '../Footer';
import { Link } from 'react-router-dom';

function ProChatPage() {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-16 pb-8 px-4 md:px-8 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-6xl font-extrabold mb-4">
                ProChat™
              </h1>
              <h2 className="text-2xl md:text-3xl font-bold text-[#191C85] mb-4">
                AI-powered Process Intelligence Agent
              </h2>
              <p className="text-xl md:text-2xl text-gray-700 font-medium">
                Get answers from your SOPs & Study documents
              </p>
            </div>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-600 text-center">
                Turn your SOPs and study folders into a secure, AI-powered knowledge hub. ProChat connects to your SOPs and SharePoint study folders so your teams get instant answers—with direct links to the source.
              </p>
            </div>
          </div>
        </section>
        {/* The Challenge Section */}
        <section className="py-1 px-4 md:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-8 text-center">
              The Challenge
            </h2>
            <p className="text-lg text-gray-600 mb-8 text-center max-w-4xl mx-auto">
              Clinical research teams struggle to find, use, and communicate critical knowledge:
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-[#191C85] mb-3">General SOP Questions</h3>
                <p className="text-gray-600">
                  Everyday policy and compliance answers buried in dense SOPs.
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-[#191C85] mb-3">Study-Specific Information</h3>
                <p className="text-gray-600">
                  Trial details, protocol deviation management plans or protocol amendments hidden in SharePoint.
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-[#191C85] mb-3">RFI/RFP Responses</h3>
                <p className="text-gray-600">
                  Manual, time-consuming process to assemble SOP-based answers under tight deadlines.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The ProChat Solution Section */}
        <section className="py-16 px-4 md:px-8 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-12 text-center">
              The ProChat Solution
            </h2>
            
            <div className="space-y-12">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="md:w-1/3">
                  <div className="bg-[#191C85] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mb-4">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-[#191C85] mb-3">General SOP Questions</h3>
                </div>
                <div className="md:w-2/3 space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Problem:</strong> Staff waste time hunting for answers to routine SOP or policy questions, leading to inefficiency and inconsistent compliance.</p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Solution:</strong> ProChat connects directly to your SOPs, enabling instant, conversational answers with page-level citations from your SOPs and controlled documents.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="md:w-1/3">
                  <div className="bg-[#191C85] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mb-4">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-[#191C85] mb-3">Study-Specific Information</h3>
                </div>
                <div className="md:w-2/3 space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Problem:</strong> Trial teams lose time digging through study folders for critical details.</p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Solution:</strong> ProChat integrates with SharePoint study folders giving teams immediate access to the right version of the right document.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="md:w-1/3">
                  <div className="bg-[#191C85] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mb-4">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-[#191C85] mb-3">RFI/RFP Responses</h3>
                </div>
                <div className="md:w-2/3 space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Problem:</strong> Responding to RFIs and RFPs requires manually assembling SOP-based processes responses, often under tight deadlines, risking errors and inconsistencies.</p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <p className="text-gray-700"><strong>Solution:</strong> ProChat automatically generates structured, SOP-based responses in the required format (e.g., word count limits)—saving hours and boosting win rates.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why It Matters Section */}
        <section className="py-16 px-4 md:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-8 text-center">
              Why It Matters
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="bg-gradient-to-r from-blue-500 to-teal-400 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700">
                  Save hours per user per week across clinical and operations teams
                </p>
              </div>
              <div className="text-center">
                <div className="bg-gradient-to-r from-blue-500 to-teal-400 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700">
                  Reduce compliance risk with consistent, source-linked answers
                </p>
              </div>
              <div className="text-center">
                <div className="bg-gradient-to-r from-blue-500 to-teal-400 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <p className="text-lg text-gray-700">
                  Increase RFP response consistency and accuracy from up-to-date sources
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ProChat Features Section */}
        <section className="py-16 px-4 md:px-8 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-12 text-center">
              ProChat Features
            </h2>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6 max-w-5xl mx-auto">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Single Sign-On (SSO)</h3>
                  <p className="text-gray-600">With role-based and defined user groups for access</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Admin Dashboard</h3>
                  <p className="text-gray-600">For permissions settings and study/project teams access management</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Monthly Usage Reports</h3>
                  <p className="text-gray-600">Customized reports with user, message and collection analytics</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Direct Integrations</h3>
                  <p className="text-gray-600">With eQMS systems (MasterControl, Veeva, etc.) and SharePoint</p>
                </div>
              </div>
              <div className="flex items-start md:col-span-2">
                <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Enterprise-Grade Security</h3>
                  <p className="text-gray-600">Hosted on Microsoft Azure giving you the same security and compliance as your Microsoft SharePoint environment</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-8 bg-[#191C85]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              See ProChat in action
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Schedule a demo today and discover how AI can provide instant answers to your organization and study project team from the knowledge in your SOPs and study documents.
            </p>
            <Link to="/#contact">
              <button className="bg-white hover:bg-gray-100 text-[#191C85] font-bold py-4 px-8 rounded-xl text-lg transition duration-300">
                Request a Demo
              </button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default ProChatPage;